import * as React from 'react';
import { InjectedRouter } from 'react-router';
import pluralize from 'pluralize';

import AdminDistrictSisDashboardCleverHeader from 'admin/rostering/components/AdminDistrictSisDashboardCleverHeader';
import AdminDistrictSisDashboardSftpHeader from 'admin/rostering/components/AdminDistrictSisDashboardSftpHeader';
import AdminSisSyncSchoolTable from 'admin/rostering/components/AdminSisSyncSchoolTable';
import CsvValidationTableContainer from 'admin/rostering/containers/CsvValidationTableContainer';
import Checkbox from 'components/Checkbox';
import Container from 'components/Container';
import CurrentUser from 'auth/CurrentUser';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import Row from 'components/Row';
import Text from 'components/Text';
import Time from 'components/Time';

import { SyncType } from 'globalTypes';
import {
  AdminDistrictSisDashboard_organizations_childOrganizations as ChildOrganization,
  AdminDistrictSisDashboard_organizations_sisSyncOverview_districtStatus as DistrictStatus,
} from 'admin/rostering/queries/__generated__/AdminDistrictSisDashboard';

import './styles/AdminDistrictSisDashboard.scss';

type Props = {
  orgUuid: string;
  districtStatus: DistrictStatus;
  childOrganizations: ChildOrganization[];
  rosteringPermissions: {
    canOnlySeeReviewScreen: boolean;
    canOnlySeeRosteringDashboard: boolean;
    canOnlySeeRosteringStagingArea: boolean;
  };
  router: InjectedRouter;
  canRosteredUsersLeaveOrg: boolean;
  handleRosteringEnforcementChange: (allowedToLeave: boolean) => void;
  fetchNewCleverData: (orgUuid: string) => void;
};

export default class AdminDistrictSisDashboard extends React.Component<Props> {
  renderHeader = () => {
    const { childOrganizations, districtStatus, orgUuid, rosteringPermissions } = this.props;
    const { canOnlySeeReviewScreen, canOnlySeeRosteringStagingArea } = rosteringPermissions;

    if (districtStatus.syncType === SyncType.clever) {
      return (
        <AdminDistrictSisDashboardCleverHeader
          orgUuid={orgUuid}
          districtStatus={districtStatus}
          childOrganizations={childOrganizations}
          canOnlySeeReviewScreen={canOnlySeeReviewScreen}
          canOnlySeeRosteringStagingArea={canOnlySeeRosteringStagingArea}
        />
      );
    } else {
      return (
        <AdminDistrictSisDashboardSftpHeader
          orgUuid={orgUuid}
          districtStatus={districtStatus}
          childOrganizations={childOrganizations}
          canOnlySeeReviewScreen={canOnlySeeReviewScreen}
          canOnlySeeRosteringStagingArea={canOnlySeeRosteringStagingArea}
        />
      );
    }
  };

  renderUploadInfo = () => {
    const { districtStatus } = this.props;
    const { lastUploadTimestamp } = districtStatus;

    if (!lastUploadTimestamp) {
      return {
        date: null,
        time: null,
      };
    }

    const date = <Time date={new Date(lastUploadTimestamp)} month="long" day="numeric" />;
    const time = (
      <Time date={new Date(lastUploadTimestamp)} hour12 hour="numeric" minute="2-digit" />
    );

    return {
      date,
      time,
    };
  };

  renderRosteringEnforcementCheckbox = () => {
    const { canRosteredUsersLeaveOrg, handleRosteringEnforcementChange } = this.props;

    return (
      <Row>
        <Container size="medium" align="left" flush>
          <Checkbox
            data-test="rostering-enforcement-checkbox"
            label="Enable rostering enforcement"
            labelNote={
              <Text color="graphite" weight="normal">
                This removes the option for SIS-linked users to opt out of organization-wide
                messages. <br />
                They can still leave individual classes within the organization.{' '}
                <Link href={LinkConstants.ROSTERING_ENFORCEMENT} target="_blank">
                  Learn more
                </Link>
              </Text>
            }
            checked={!canRosteredUsersLeaveOrg}
            onChange={() => handleRosteringEnforcementChange(!canRosteredUsersLeaveOrg)}
          />
        </Container>
      </Row>
    );
  };

  renderCleverSyncInfo = () => {
    const { orgUuid, fetchNewCleverData } = this.props;
    const { date, time } = this.renderUploadInfo();

    return (
      <React.Fragment>
        <Text>
          Last Clever sync:{' '}
          {!date ? (
            <Text> -- </Text>
          ) : (
            <Text>
              <Text weight="semibold">
                {date}, {time}
              </Text>
              {' | '}
              <Link onClick={() => fetchNewCleverData(orgUuid)} weight="semibold">
                Check for new Clever data
              </Link>
              {' | '}
              <Link href={LinkConstants.SIS_SYNC_HELP} target="_blank">
                Get help
              </Link>{' '}
            </Text>
          )}
        </Text>
        <Link
          weight="semibold"
          href={`/sync_setup?district_uuid=${orgUuid}`}
          className="setup-sync-settings-button">
          Clever setup
        </Link>
      </React.Fragment>
    );
  };

  renderSftpUploadInfo = () => {
    const { orgUuid, districtStatus } = this.props;
    const { filesZipUrl, lastUploadFiles } = districtStatus;
    const { date, time } = this.renderUploadInfo();

    return (
      <React.Fragment>
        <Text>
          Last Upload:{' '}
          {!date ? (
            <Text> -- </Text>
          ) : (
            <Text>
              <Text weight="semibold">
                {date}, {time}
              </Text>
              {' | '}
              <Link weight="semibold" href={filesZipUrl || '#'}>
                Download {pluralize('file', lastUploadFiles.length)}
              </Link>
              {' | '}
              <Link weight="semibold" href={LinkConstants.SIS_SYNC_HELP} target="_blank">
                Get help
              </Link>{' '}
            </Text>
          )}
        </Text>
        <Link
          weight="semibold"
          href={`/sftp_sync_setup/details?district_uuid=${orgUuid}`}
          className="setup-sync-settings-button">
          SFTP credentials
        </Link>
      </React.Fragment>
    );
  };

  renderUploadInfoAndSettings = () => {
    const { orgUuid, districtStatus, fetchNewCleverData } = this.props;
    const { syncType } = districtStatus;

    return (
      <Container align="left" full flush>
        <Row size="xsmall" data-test="last-upload-info-row">
          {syncType === SyncType.clever ? this.renderCleverSyncInfo() : this.renderSftpUploadInfo()}
        </Row>
      </Container>
    );
  };

  renderCsvValidationTable = () => {
    const {
      districtStatus: { syncType },
      orgUuid,
    } = this.props;

    if (syncType !== SyncType.clever && syncType !== SyncType.none) {
      return (
        <Row>
          <CsvValidationTableContainer orgUuid={orgUuid} />
        </Row>
      );
    }
  };

  renderBody = () => {
    const { childOrganizations, orgUuid, rosteringPermissions, router } = this.props;
    const { canOnlySeeRosteringDashboard, canOnlySeeReviewScreen } = rosteringPermissions;
    const currentUser = CurrentUser.getUser();

    return (
      <section className="AdminAdminsView">
        {this.renderHeader()}
        {canOnlySeeRosteringDashboard &&
          currentUser.hasFeature('rostering_enforcement_checkbox.web.sept-19') &&
          this.renderRosteringEnforcementCheckbox()}
        {this.renderUploadInfoAndSettings()}
        {this.renderCsvValidationTable()}
        <Row>
          <AdminSisSyncSchoolTable
            orgUuid={orgUuid}
            canOnlySeeReviewScreen={canOnlySeeReviewScreen}
            router={router}
            childOrgs={childOrganizations}
          />
        </Row>
      </section>
    );
  };

  render() {
    return <div className="AdminDistrictSisDashboard">{this.renderBody()}</div>;
  }
}
